/**
 * Google Analytics Service for React App
 * Handles custom event tracking and user behavior analytics
 */

/* global gtag */

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.userId = null;
    this.organizationId = null;
  }

  // Initialize analytics with user context
  initialize(user, organization) {
    if (typeof gtag !== 'undefined') {
      this.userId = user?.id;
      this.organizationId = organization?.id;
      this.isInitialized = true;

      // Set user dimensions
      if (this.userId) {
        gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
          user_id: this.userId,
          custom_map: {
            custom_dimension_1: 'user_role',
            custom_dimension_2: 'organization_size',
            custom_dimension_3: 'plan_type'
          }
        });
      }
    }
  }

  // Track custom events
  track(eventName, parameters = {}) {
    if (!this.isInitialized || typeof gtag === 'undefined') return;

    gtag('event', eventName, {
      ...parameters,
      user_id: this.userId,
      organization_id: this.organizationId,
      timestamp: new Date().toISOString()
    });
  }

  // Track page views for SPA navigation
  trackPageView(path, title) {
    if (typeof gtag !== 'undefined') {
      gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
        page_path: path,
        page_title: title || document.title
      });
    }
  }

  // Feature-specific tracking methods
  trackFeatureCreated(featureType, source = 'manual') {
    this.track('feature_created', {
      feature_type: featureType,
      creation_source: source,
      organization_id: this.organizationId
    });
  }

  trackFeedbackSubmitted(feedbackType, isPublic = false) {
    this.track('feedback_submitted', {
      feedback_type: feedbackType,
      is_public: isPublic,
      organization_id: this.organizationId
    });
  }

  trackTeamMemberInvited(teamSize) {
    this.track('team_member_invited', {
      team_size: teamSize,
      organization_id: this.organizationId
    });
  }

  trackRoadmapUpdated(updateType, itemCount) {
    this.track('roadmap_updated', {
      update_type: updateType,
      item_count: itemCount,
      organization_id: this.organizationId
    });
  }

  trackUserInteraction(action, component, context = {}) {
    this.track('user_interaction', {
      action: action,
      component: component,
      ...context,
      organization_id: this.organizationId
    });
  }

  // Track user engagement metrics
  trackSessionDuration(duration) {
    this.track('session_duration', {
      duration_seconds: duration,
      organization_id: this.organizationId
    });
  }

  trackFeatureUsage(featureName, usageType = 'view') {
    this.track('feature_usage', {
      feature_name: featureName,
      usage_type: usageType,
      organization_id: this.organizationId
    });
  }

  // Track conversion events
  trackSignup(method = 'email') {
    this.track('signup_completed', {
      signup_method: method,
      timestamp: new Date().toISOString()
    });
  }

  trackOrganizationCreated(planType = 'free') {
    this.track('organization_created', {
      plan_type: planType,
      timestamp: new Date().toISOString()
    });
  }

  // Track error events
  trackError(errorType, errorMessage, context = {}) {
    this.track('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      ...context,
      organization_id: this.organizationId
    });
  }

  // Track performance metrics
  trackPerformance(metricName, value, context = {}) {
    this.track('performance_metric', {
      metric_name: metricName,
      metric_value: value,
      ...context,
      organization_id: this.organizationId
    });
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
